import axios, { type AxiosInstance } from 'axios';

const ACCESS_KEY = 'ro_access';
const REFRESH_KEY = 'ro_refresh';

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/**
 * Axios instance for the Core API. Attaches the bearer token and, on a 401,
 * transparently rotates the refresh token once and retries the original request
 * — so an expired access token is invisible to the UI.
 */
export const api: AxiosInstance = axios.create({ baseURL: '/' });

/** Super-admin impersonation ("open as company") — the active tenant override.
 * Only a hint: the server grants access purely from the caller's DB-verified
 * platform role, so this is inert for everyone else. */
const ACT_AS_KEY = 'ro_act_as';
export const actAsStore = {
  get id() {
    return localStorage.getItem(ACT_AS_KEY);
  },
  set(orgId: string) {
    localStorage.setItem(ACT_AS_KEY, orgId);
  },
  clear() {
    localStorage.removeItem(ACT_AS_KEY);
  },
};

api.interceptors.request.use((config) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const actAs = actAsStore.id;
  if (actAs) config.headers['x-ro-act-as'] = actAs;
  return config;
});

let refreshing: Promise<string> | null = null;

/** Force a token rotation (deduped with the interceptor's in-flight refresh). */
function rotate(): Promise<string> {
  refreshing ??= axios
    .post('/v1/auth/refresh', { refreshToken: tokenStore.refresh })
    .then((res) => {
      tokenStore.set(res.data.accessToken, res.data.refreshToken);
      return res.data.accessToken as string;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/** Seconds until the JWT's `exp`, or 0 if unparseable/expired. */
function secondsToExpiry(jwt: string): number {
  try {
    const { exp } = JSON.parse(atob(jwt.split('.')[1]!));
    return exp - Math.floor(Date.now() / 1000);
  } catch {
    return 0;
  }
}

/**
 * Returns a valid access token, rotating it first if it's expired or about to
 * expire. Used by the live WebSocket, which — unlike axios calls — has no 401
 * interceptor to transparently refresh a stale token mid-session.
 */
export async function ensureAccessToken(): Promise<string | null> {
  const token = tokenStore.access;
  if (token && secondsToExpiry(token) > 30) return token;
  if (!tokenStore.refresh) return token;
  try {
    return await rotate();
  } catch {
    return token; // let the caller fail visibly rather than throw here
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && tokenStore.refresh) {
      original._retry = true;
      try {
        const newAccess = await rotate();
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch {
        tokenStore.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);
